import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  View, Image, StyleSheet, Animated, PanResponder, Dimensions,
  Platform, Modal, TouchableWithoutFeedback, TouchableOpacity,
  Text, TextInput, ScrollView,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../context/ThemeContext';
import { slugify } from '../../utils/urlHelpers';

const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');

// ── Size configs ──────────────────────────────────────────────────────────────
const SIZE_CFG = {
  small:  { image: 65,  wrap: 85  },
  normal: { image: 90,  wrap: 110 },
  big:    { image: 115, wrap: 140 },
};

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:        'rgba(9, 9, 14, 0.98)',
  border:    'rgba(255, 107, 53, 0.22)',
  orange:    '#FF6B35',
  orangeGlow:'rgba(255, 107, 53, 0.14)',
  orangeDim: 'rgba(255, 107, 53, 0.08)',
  white:     '#FFFFFF',
  textSec:   'rgba(255,255,255,0.42)',
  divider:   'rgba(255,255,255,0.06)',
  surface:   'rgba(255,255,255,0.05)',
  danger:    '#FF4B4B',
};

const MENU_W = Math.min(370, SCREEN_W - 16);

// ── Light-mode palette ────────────────────────────────────────────────────────
const CL = {
  bg:        'rgba(255,255,255,0.98)',
  border:    'rgba(255,107,53,0.3)',
  orange:    '#FF6B35',
  orangeGlow:'rgba(255,107,53,0.1)',
  orangeDim: 'rgba(255,107,53,0.07)',
  white:     '#111827',
  textSec:   'rgba(0,0,0,0.42)',
  divider:   'rgba(0,0,0,0.09)',
  surface:   'rgba(0,0,0,0.04)',
  danger:    '#DC2626',
};

// ── Emotion reason message ────────────────────────────────────────────────────
function getEmotionReason(emotion, { currentStreak, enrollmentCount, unreadCount, quizScore }) {
  switch (emotion) {
    case 'skull':      return 'Something went wrong loading your data.';
    case 'thinking':   return 'Loading your profile...';
    case 'mind_blown': return 'You scored 100% on your last quiz!';
    case 'love':       return `Great quiz score of ${quizScore}%. Keep it up!`;
    case 'crying':
      if (quizScore != null && quizScore < 50) return `Your quiz score was ${quizScore}%. Keep practicing.`;
      if (currentStreak > 0) return 'Your streak just broke. Start a fresh one today.';
      return 'No active streak. Start learning to build one!';
    case 'party':      return `${currentStreak} days in a row — you are unstoppable!`;
    case 'laughing':   return `${currentStreak}-day streak! You are on fire.`;
    case 'cheeky':     return `${currentStreak}-day streak! Keep the momentum going.`;
    case 'detective':  return enrollmentCount === 0
      ? 'You have not enrolled in any course yet. Browse what is available!'
      : 'Looks like you are exploring. Find a course to dive into.';
    case 'nervous':    return `You have ${unreadCount} unread notifications waiting.`;
    default:           return 'Ready to keep learning today?';
  }
}

// ── Storage keys ──────────────────────────────────────────────────────────────
const SK = {
  SIZE:    '@spherix:size',
  HIDDEN:  '@spherix:hidden',
  GOAL:    '@spherix:goal',
  EMOTION: '@spherix:emotion',
};

// ── Corners (bottom only) ─────────────────────────────────────────────────────
function makeCorners(wrap) {
  return [
    { x: 0,                y: SCREEN_H - wrap },
    { x: SCREEN_W - wrap,  y: SCREEN_H - wrap },
  ];
}
function nearestCorner(x, y, wrap) {
  return makeCorners(wrap).reduce((best, c) =>
    Math.hypot(x - c.x, y - c.y) < Math.hypot(x - best.x, y - best.y) ? c : best
  );
}

// ── Pose images ───────────────────────────────────────────────────────────────
const POSES = {
  angel:      require('../../assets/mascot/angel.png'),
  angry:      require('../../assets/mascot/angry.png'),
  cheeky:     require('../../assets/mascot/cheeky.png'),
  crying:     require('../../assets/mascot/crying.png'),
  detective:  require('../../assets/mascot/detective.png'),
  devil:      require('../../assets/mascot/devil.png'),
  frozen:     require('../../assets/mascot/frozen.png'),
  gamer:      require('../../assets/mascot/gamer.png'),
  laughing:   require('../../assets/mascot/laughing.png'),
  love:       require('../../assets/mascot/love.png'),
  melting:    require('../../assets/mascot/melting.png'),
  mind_blown: require('../../assets/mascot/mind_blown.png'),
  nervous:    require('../../assets/mascot/nervous.png'),
  neutral:    require('../../assets/mascot/neutral.png'),
  party:      require('../../assets/mascot/party.png'),
  skull:      require('../../assets/mascot/skull.png'),
  steam:      require('../../assets/mascot/steam.png'),
  thinking:   require('../../assets/mascot/thinking.png'),
};

const POSE_KEYS = Object.keys(POSES);

const EMOTION_LABEL = {
  neutral:'Chillin\'', angry:'Angry', angel:'Angelic', cheeky:'Cheeky',
  crying:'Sad', detective:'Curious', devil:'Mischievous', frozen:'Frozen',
  gamer:'Gaming Mode', laughing:'Hyped', love:'Loving it', melting:'Melting',
  mind_blown:'Mind Blown', nervous:'Nervous', party:'Celebrating',
  skull:'Dead inside', steam:'Steaming', thinking:'Thinking...',
};

const EMOTION_DOT = {
  neutral:'#94A3B8', angry:'#EF4444', angel:'#FCD34D', cheeky:'#FB923C',
  crying:'#60A5FA', detective:'#A78BFA', devil:'#DC2626', frozen:'#67E8F9',
  gamer:'#34D399', laughing:'#FBBF24', love:'#F472B6', melting:'#FB923C',
  mind_blown:'#C084FC', nervous:'#FDE047', party:'#FF6B35',
  skull:'#6B7280', steam:'#9CA3AF', thinking:'#93C5FD',
};

// ── Emotion selection ─────────────────────────────────────────────────────────
function selectEmotion({ isStreakAlive, currentStreak, enrollmentCount, unreadCount, quizScore, isLoading, hasError }) {
  if (hasError)                             return 'skull';
  if (isLoading)                            return 'thinking';
  if (quizScore === 100)                    return 'mind_blown';
  if (quizScore != null && quizScore >= 70) return 'love';
  if (quizScore != null && quizScore < 50)  return 'crying';
  if (isStreakAlive && currentStreak >= 30) return 'party';
  if (isStreakAlive && currentStreak >= 7)  return 'laughing';
  if (isStreakAlive && currentStreak >= 1)  return 'cheeky';
  if (!isStreakAlive && currentStreak > 0)  return 'crying';
  if (enrollmentCount === 0)                return 'detective';
  if (unreadCount > 3)                      return 'nervous';
  return 'neutral';
}

// ── FAQ items ─────────────────────────────────────────────────────────────────
const FAQ = [
  { q: 'How do I enroll in a course?',    a: 'Tap Browse Courses, find something you like, and press Enroll on the course page.' },
  { q: 'How do streaks work?',            a: 'Complete at least one lesson every day to keep your streak alive. Miss a day — it resets.' },
  { q: 'How do I earn a certificate?',    a: 'Finish all lessons and pass the final quiz with 70% or higher.' },
  { q: 'Can I retake a quiz?',            a: 'Yes! Retake any quiz as many times as you need to improve your score.' },
  { q: 'How does AI Tutor work?',         a: 'AI Tutor creates personalized lessons on any topic. Find it in the courses section.' },
];

// ── Body animation hook ───────────────────────────────────────────────────────
function useBodyAnim(emotion) {
  const tx  = useRef(new Animated.Value(0)).current;
  const ty  = useRef(new Animated.Value(0)).current;
  const rot = useRef(new Animated.Value(0)).current;
  const sc  = useRef(new Animated.Value(1)).current;
  const sy  = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    [tx, ty, rot, sc, sy].forEach(v => v.stopAnimation());
    tx.setValue(0); ty.setValue(0); rot.setValue(0); sc.setValue(1); sy.setValue(1);

    let anim;
    const loop = (a) => { anim = Animated.loop(a); anim.start(); };
    const once = (a) => { anim = a; anim.start(); };

    switch (emotion) {
      case 'neutral': case 'steam':
        loop(Animated.sequence([
          Animated.timing(ty, { toValue: -7,  duration: 1200, useNativeDriver: true }),
          Animated.timing(ty, { toValue:  0,  duration: 1200, useNativeDriver: true }),
        ])); break;
      case 'angry':
        loop(Animated.sequence([
          Animated.timing(tx, { toValue: -5, duration: 65, useNativeDriver: true }),
          Animated.timing(tx, { toValue:  5, duration: 65, useNativeDriver: true }),
          Animated.timing(tx, { toValue:  0, duration: 65, useNativeDriver: true }),
        ])); break;
      case 'gamer':
        loop(Animated.sequence([
          Animated.timing(tx, { toValue: -3, duration: 50, useNativeDriver: true }),
          Animated.timing(tx, { toValue:  3, duration: 50, useNativeDriver: true }),
        ])); break;
      case 'nervous':
        loop(Animated.sequence([
          Animated.timing(tx, { toValue: -2, duration: 55, useNativeDriver: true }),
          Animated.timing(tx, { toValue:  2, duration: 55, useNativeDriver: true }),
        ])); break;
      case 'cheeky': case 'laughing':
        loop(Animated.sequence([
          Animated.timing(ty, { toValue: -11, duration: 320, useNativeDriver: true }),
          Animated.timing(ty, { toValue:   0, duration: 320, useNativeDriver: true }),
        ])); break;
      case 'party':
        loop(Animated.sequence([
          Animated.timing(ty, { toValue: -20, duration: 340, useNativeDriver: true }),
          Animated.timing(ty, { toValue:   0, duration: 340, useNativeDriver: true }),
        ])); break;
      case 'angel':
        loop(Animated.sequence([
          Animated.timing(ty, { toValue: -13, duration: 1800, useNativeDriver: true }),
          Animated.timing(ty, { toValue:   0, duration: 1800, useNativeDriver: true }),
        ])); break;
      case 'crying':
        loop(Animated.sequence([
          Animated.timing(rot, { toValue: -3, duration: 900, useNativeDriver: true }),
          Animated.timing(rot, { toValue:  3, duration: 900, useNativeDriver: true }),
        ])); break;
      case 'detective': case 'thinking':
        loop(Animated.sequence([
          Animated.timing(rot, { toValue: -5, duration: 1600, useNativeDriver: true }),
          Animated.timing(rot, { toValue:  5, duration: 1600, useNativeDriver: true }),
        ])); break;
      case 'love': case 'devil':
        loop(Animated.sequence([
          Animated.timing(sc, { toValue: 1.07, duration: 700, useNativeDriver: true }),
          Animated.timing(sc, { toValue: 1.0,  duration: 700, useNativeDriver: true }),
        ])); break;
      case 'mind_blown':
        loop(Animated.sequence([
          Animated.timing(sc, { toValue: 1.22, duration: 280, useNativeDriver: true }),
          Animated.timing(sc, { toValue: 0.95, duration: 200, useNativeDriver: true }),
          Animated.timing(sc, { toValue: 1.08, duration: 200, useNativeDriver: true }),
          Animated.timing(sc, { toValue: 1.0,  duration: 200, useNativeDriver: true }),
          Animated.delay(900),
        ])); break;
      case 'skull':
        once(Animated.timing(rot, { toValue: -8, duration: 500, useNativeDriver: true })); break;
      case 'melting':
        loop(Animated.sequence([
          Animated.timing(sy, { toValue: 0.84, duration: 2400, useNativeDriver: true }),
          Animated.timing(sy, { toValue: 1.0,  duration: 2400, useNativeDriver: true }),
        ])); break;
      default: break;
    }
    return () => anim?.stop();
  }, [emotion]); // eslint-disable-line react-hooks/exhaustive-deps

  const rotDeg = rot.interpolate({ inputRange: [-20, 20], outputRange: ['-20deg', '20deg'] });
  return { transform: [{ translateX: tx }, { translateY: ty }, { rotate: rotDeg }, { scale: sc }, { scaleY: sy }] };
}

// ── Particles ─────────────────────────────────────────────────────────────────
const PARTICLE_SETS = {
  crying:     { chars: ['💧','💧','💧'],      offsets:[[-14,8],[0,6],[14,8]],              yEnd:52,  pulse:false },
  love:       { chars: ['❤️','🧡','❤️'],     offsets:[[-18,8],[0,6],[18,8]],              yEnd:-60, pulse:false },
  party:      { chars: ['✨','⭐','💫','✨'],  offsets:[[-24,-4],[-8,-6],[8,-6],[24,-4]],  yEnd:-58, pulse:false },
  angry:      { chars: ['💢','💢'],           offsets:[[-20,-28],[20,-28]],               yEnd:0,   pulse:true  },
  frozen:     { chars: ['❄️','❄️','❄️'],    offsets:[[-20,-8],[0,-12],[20,-8]],          yEnd:52,  pulse:false },
  angel:      { chars: ['✨','✨','✨'],       offsets:[[-30,2],[30,2],[0,-34]],            yEnd:-52, pulse:false },
  steam:      { chars: ['💨','💨'],           offsets:[[-14,8],[14,8]],                   yEnd:-52, pulse:false },
  mind_blown: { chars: ['💥','✨','💥'],      offsets:[[-28,-12],[28,-12],[0,-32]],        yEnd:0,   pulse:true  },
  devil:      { chars: ['🔥','🔥'],           offsets:[[-14,30],[14,30]],                 yEnd:-32, pulse:false },
  nervous:    { chars: ['💧','💧'],           offsets:[[-10,-22],[10,-22]],               yEnd:22,  pulse:false },
};

function Particle({ char, ox, oy, yEnd, delay, pulse, wrapSize }) {
  const tx = useRef(new Animated.Value(0)).current;
  const ty = useRef(new Animated.Value(0)).current;
  const op = useRef(new Animated.Value(0)).current;
  const sc = useRef(new Animated.Value(0.6)).current;

  useEffect(() => {
    let anim;
    if (pulse) {
      anim = Animated.loop(Animated.sequence([
        Animated.delay(delay),
        Animated.parallel([
          Animated.sequence([
            Animated.timing(sc, { toValue: 1.5, duration: 280, useNativeDriver: true }),
            Animated.timing(sc, { toValue: 1.0, duration: 280, useNativeDriver: true }),
          ]),
          Animated.sequence([
            Animated.timing(op, { toValue: 1,   duration: 140, useNativeDriver: true }),
            Animated.timing(op, { toValue: 0.3, duration: 420, useNativeDriver: true }),
          ]),
        ]),
      ]));
    } else {
      anim = Animated.loop(Animated.sequence([
        Animated.delay(delay),
        Animated.parallel([
          Animated.timing(ty, { toValue: yEnd, duration: 1100, useNativeDriver: true }),
          Animated.sequence([
            Animated.timing(op, { toValue: 1,   duration: 260, useNativeDriver: true }),
            Animated.timing(op, { toValue: 0,   duration: 840, useNativeDriver: true }),
          ]),
          Animated.timing(sc, { toValue: 1.1, duration: 550, useNativeDriver: true }),
        ]),
        Animated.parallel([
          Animated.timing(ty, { toValue: 0,   duration: 0, useNativeDriver: true }),
          Animated.timing(op, { toValue: 0,   duration: 0, useNativeDriver: true }),
          Animated.timing(sc, { toValue: 0.6, duration: 0, useNativeDriver: true }),
        ]),
      ]));
    }
    anim.start();
    return () => anim.stop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const half = wrapSize / 2;
  return (
    <Animated.Text style={{ position:'absolute', fontSize:16, left:half+ox-9, top:half+oy-9, opacity:op, transform:[{translateX:tx},{translateY:ty},{scale:sc}] }}>
      {char}
    </Animated.Text>
  );
}

function ParticleOverlay({ emotion, wrapSize }) {
  const cfg = PARTICLE_SETS[emotion];
  if (!cfg) return null;
  return (
    <>
      {cfg.chars.map((char, i) => (
        <Particle key={i} char={char} ox={cfg.offsets[i][0]} oy={cfg.offsets[i][1]}
          yEnd={cfg.yEnd} delay={i * 320} pulse={cfg.pulse} wrapSize={wrapSize} />
      ))}
    </>
  );
}

// ── Quick links + questions ───────────────────────────────────────────────────
const QUICK_LINKS = [
  { id: 'continue', label: 'Continue Learning' },
  { id: 'courses',  label: 'My Courses'        },
  { id: 'browse',   label: 'Browse Courses'    },
];

const QUESTIONS = [
  { id: 'q_cert',   label: 'How do I earn a certificate?',  answer: 'Finish all lessons in a course and pass the final quiz with 70% or higher. Your certificate will be available in your profile.' },
  { id: 'q_streak', label: 'How do streaks work?',          answer: 'Complete at least one lesson every day to keep your streak alive. Missing a day resets your streak back to zero.' },
  { id: 'q_ai',     label: 'How does the AI Tutor work?',   answer: 'AI Tutor generates a personalized lesson on any topic you choose. Find it in the Courses section under AI Tutor.' },
  { id: 'q_quiz',   label: 'Can I retake a quiz?',          answer: 'Yes. You can retake any quiz as many times as you need. Only your highest score is saved.' },
  { id: 'q_enroll', label: 'How do I enroll in a course?',  answer: 'Open any course from Browse Courses and tap the Enroll button on the course detail page.' },
];

// ── Typing indicator dots ─────────────────────────────────────────────────────
function TypingDots({ orange }) {
  const d = [
    useRef(new Animated.Value(0)).current,
    useRef(new Animated.Value(0)).current,
    useRef(new Animated.Value(0)).current,
  ];
  useEffect(() => {
    const anims = d.map((v, i) =>
      Animated.loop(Animated.sequence([
        Animated.delay(i * 140),
        Animated.timing(v, { toValue: -5, duration: 260, useNativeDriver: true }),
        Animated.timing(v, { toValue:  0, duration: 260, useNativeDriver: true }),
        Animated.delay(500),
      ]))
    );
    anims.forEach(a => a.start());
    return () => anims.forEach(a => a.stop());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <View style={mu.dotsRow}>
      {d.map((v, i) => (
        <Animated.View key={i} style={[mu.dot, { backgroundColor: orange, transform: [{ translateY: v }] }]} />
      ))}
    </View>
  );
}

// ── Size toggle ───────────────────────────────────────────────────────────────
function SizeToggle({ value, onChange, th }) {
  return (
    <View style={mu.sizeRow}>
      <Text style={[mu.sizeLabel, { color: th.textSec }]}>Size</Text>
      <View style={mu.sizeButtons}>
        {['small', 'normal', 'big'].map(s => (
          <TouchableOpacity key={s} onPress={() => onChange(s)}
            style={[mu.sizeBtn,
              { backgroundColor: value === s ? th.orange : th.surface, borderColor: value === s ? th.orange : th.divider },
            ]}>
            <Text style={[mu.sizeBtnText, { color: value === s ? '#fff' : th.textSec }]}>
              {s === 'small' ? 'S' : s === 'normal' ? 'M' : 'L'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

// ── Main menu card ────────────────────────────────────────────────────────────
function SpherixMenu({ displayEmotion, enrollments, courses,
  size, onSizeChange, onHide, onClose, isDark, emotionReason }) {

  const th = isDark ? C : CL;
  const navigation = useNavigation();
  const [isTyping,       setIsTyping]       = useState(false);
  const [selectedId,     setSelectedId]     = useState(null);
  const [displayedText,  setDisplayedText]  = useState('');
  const [showAnswer,     setShowAnswer]     = useState(false);
  const typingTimer     = useRef(null);
  const typewriterTimer = useRef(null);

  useEffect(() => () => {
    clearTimeout(typingTimer.current);
    clearInterval(typewriterTimer.current);
  }, []);

  const triggerAnswer = useCallback((answer) => {
    setShowAnswer(false);
    setDisplayedText('');
    setIsTyping(true);
    clearTimeout(typingTimer.current);
    clearInterval(typewriterTimer.current);
    typingTimer.current = setTimeout(() => {
      setIsTyping(false);
      setShowAnswer(true);
      let i = 0;
      typewriterTimer.current = setInterval(() => {
        i++;
        setDisplayedText(answer.slice(0, i));
        if (i >= answer.length) clearInterval(typewriterTimer.current);
      }, 16);
    }, 1300);
  }, []);

  const handleQuickLink = useCallback((id) => {
    setSelectedId(id);
    if (id === 'continue') {
      const e = enrollments?.find(en => (en.progress || 0) < 100) || enrollments?.[0];
      if (!e) { navigation.navigate('Courses'); onClose(); return; }
      const course = courses?.find(c => c.id === (e.courseId || e.id));
      navigation.navigate('CourseDetail', { courseId: e.courseId || e.id, courseName: slugify(course?.name || 'Course') });
      onClose();
    } else if (id === 'courses') {
      navigation.navigate('EnrolledCourses'); onClose();
    } else {
      navigation.navigate('Courses'); onClose();
    }
  }, [enrollments, courses, navigation, onClose]);

  const handleQuestion = useCallback((q) => {
    setSelectedId(q.id);
    triggerAnswer(q.answer);
  }, [triggerAnswer]);

  const bubbleStyle     = { backgroundColor: th.surface, borderColor: th.divider };
  const qCardBaseStyle  = { backgroundColor: th.surface, borderColor: th.divider };
  const qCardActiveStyle = { backgroundColor: th.orangeGlow, borderColor: th.orange };

  return (
    <View style={[mu.card, { backgroundColor: th.bg, borderColor: th.border }]}>
      {/* Header */}
      <View style={[mu.header, { borderBottomColor: th.divider }]}>
        <View style={[mu.headerGlow, { backgroundColor: th.orangeDim, borderColor: th.border }]}>
          <Image source={POSES[displayEmotion]} style={mu.headerImg} resizeMode="contain" />
        </View>
        <View style={mu.headerText}>
          <Text style={[mu.headerName, { color: th.orange }]}>SPHERIX</Text>
          <Text style={[mu.headerSub, { color: th.textSec }]}>SkillSphere assistant</Text>
        </View>
        <TouchableOpacity onPress={onClose} style={[mu.closeBtn, { backgroundColor: th.surface }]}
          hitSlop={{ top:10, left:10, bottom:10, right:10 }}>
          <Text style={[mu.closeTxt, { color: th.textSec }]}>x</Text>
        </TouchableOpacity>
      </View>

      {/* Body: chat left | cards right */}
      <View style={mu.body}>

        {/* Chat panel */}
        <ScrollView style={[mu.chatPanel, { backgroundColor: isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)' }]}
          showsVerticalScrollIndicator={false} bounces={false}>

          {/* Emotion reason bubble */}
          <View style={mu.bubbleRow}>
            <View style={[mu.bubbleAvatar, { backgroundColor: th.orangeDim }]}>
              <Image source={POSES[displayEmotion]} style={mu.bubbleAvatarImg} resizeMode="contain" />
            </View>
            <View style={[mu.bubble, bubbleStyle]}>
              <Text style={[mu.bubbleText, { color: th.white }]}>{emotionReason}</Text>
            </View>
          </View>

          {/* Typing indicator */}
          {isTyping && (
            <View style={mu.bubbleRow}>
              <View style={[mu.bubbleAvatar, { backgroundColor: th.orangeDim }]}>
                <Image source={POSES[displayEmotion]} style={mu.bubbleAvatarImg} resizeMode="contain" />
              </View>
              <View style={[mu.bubble, bubbleStyle]}>
                <TypingDots orange={th.orange} />
              </View>
            </View>
          )}

          {/* Answer */}
          {showAnswer && displayedText.length > 0 && (
            <View style={mu.bubbleRow}>
              <View style={[mu.bubbleAvatar, { backgroundColor: th.orangeDim }]}>
                <Image source={POSES[displayEmotion]} style={mu.bubbleAvatarImg} resizeMode="contain" />
              </View>
              <View style={[mu.bubble, bubbleStyle]}>
                <Text style={[mu.bubbleText, { color: th.white }]}>{displayedText}</Text>
              </View>
            </View>
          )}
        </ScrollView>

        <View style={[mu.dividerV, { backgroundColor: th.divider }]} />

        {/* Cards panel */}
        <ScrollView style={mu.cardsPanel} showsVerticalScrollIndicator={false} bounces={false}>
          <Text style={[mu.cardsSectionLabel, { color: th.orange }]}>QUICK LINKS</Text>
          {QUICK_LINKS.map(link => (
            <TouchableOpacity key={link.id} onPress={() => handleQuickLink(link.id)}
              style={[mu.qCard, selectedId === link.id ? qCardActiveStyle : qCardBaseStyle]}
              activeOpacity={0.75}>
              <Text style={[mu.qCardText, { color: th.white }]}>{link.label}</Text>
              <Text style={[mu.qCardChevron, { color: th.textSec }]}>›</Text>
            </TouchableOpacity>
          ))}

          <Text style={[mu.cardsSectionLabel, { color: th.orange, marginTop: 10 }]}>ASK SPHERIX</Text>
          {QUESTIONS.map(q => (
            <TouchableOpacity key={q.id} onPress={() => handleQuestion(q)}
              style={[mu.qCard, selectedId === q.id ? qCardActiveStyle : qCardBaseStyle]}
              activeOpacity={0.75}>
              <Text style={[mu.qCardText, { color: th.white }]}>{q.label}</Text>
              <Text style={[mu.qCardChevron, { color: th.textSec }]}>›</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Footer: hide + size */}
      <View style={[mu.footer, { borderTopColor: th.divider }]}>
        <TouchableOpacity onPress={onHide} style={[mu.hideBtn, {
          backgroundColor: isDark ? 'rgba(255,75,75,0.1)' : 'rgba(220,38,38,0.08)',
          borderColor:     isDark ? 'rgba(255,75,75,0.22)' : 'rgba(220,38,38,0.2)',
        }]} activeOpacity={0.8}>
          <Text style={[mu.hideBtnText, { color: th.danger }]}>Hide Spherix</Text>
        </TouchableOpacity>
        <SizeToggle value={size} onChange={onSizeChange} th={th} />
      </View>
    </View>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function DashboardMascot({
  currentStreak   = 0,
  isStreakAlive   = false,
  enrollmentCount = 0,
  unreadCount     = 0,
  quizScore       = null,
  isLoading       = false,
  hasError        = false,
  enrollments     = [],
  courses         = [],
}) {
  const { isDark } = useTheme();

  const [size,          setSize]          = useState('normal');
  const [isHidden,      setIsHidden]      = useState(false);
  const [manualEmotion, setManualEmotion] = useState(null);
  const [dragEmotion,   setDragEmotion]   = useState(null);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [goal,          setGoal]          = useState('');

  const menuAnim   = useRef(new Animated.Value(0)).current;
  const settleTimer = useRef(null);
  const isDragging  = useRef(false);
  const menuPos     = useRef({ bottom: 110, right: 16 });
  const animMoveRef = useRef(null);

  const cfg      = SIZE_CFG[size] || SIZE_CFG.normal;
  const INIT_X   = SCREEN_W - cfg.wrap - 16;
  const INIT_Y   = SCREEN_H - cfg.wrap - 90;

  const pan = useRef(new Animated.ValueXY({ x: INIT_X, y: INIT_Y })).current;

  // Keep move handler stable
  animMoveRef.current = Animated.event(
    [null, { dx: pan.x, dy: pan.y }],
    { useNativeDriver: false }
  );

  // Load persisted preferences
  useEffect(() => {
    AsyncStorage.multiGet([SK.SIZE, SK.HIDDEN, SK.GOAL, SK.EMOTION]).then(pairs => {
      const m = Object.fromEntries(pairs);
      if (m[SK.SIZE])    setSize(m[SK.SIZE]);
      if (m[SK.HIDDEN] === 'true') setIsHidden(true);
      if (m[SK.GOAL])    setGoal(m[SK.GOAL]);
      if (m[SK.EMOTION]) setManualEmotion(m[SK.EMOTION]);
    }).catch(() => {});
  }, []);

  // Menu open/close animation
  const openMenu = useCallback(() => {
    // Snapshot position for menu placement
    const mx = pan.x._value;
    const my = pan.y._value;
    const menuRight  = Math.max(8, Math.min(SCREEN_W - MENU_W - 8, SCREEN_W - mx - cfg.wrap));
    const menuBottom = Math.max(8, SCREEN_H - my + 6);
    menuPos.current  = { bottom: menuBottom, right: menuRight };

    setMenuOpen(true);
    Animated.spring(menuAnim, { toValue: 1, useNativeDriver: true, tension: 80, friction: 10 }).start();
  }, [pan, cfg, menuAnim]);

  const closeMenu = useCallback(() => {
    Animated.timing(menuAnim, { toValue: 0, duration: 160, useNativeDriver: true }).start(() => setMenuOpen(false));
  }, [menuAnim]);

  // PanResponder refs (avoid stale closures)
  const grantRef   = useRef(null);
  const moveRef    = useRef(null);
  const releaseRef = useRef(null);

  grantRef.current = () => {
    isDragging.current = false;
    if (settleTimer.current) { clearTimeout(settleTimer.current); settleTimer.current = null; }
    pan.stopAnimation();
    pan.setOffset({ x: pan.x._value, y: pan.y._value });
    pan.setValue({ x: 0, y: 0 });
  };

  moveRef.current = (e, gesture) => {
    if (!isDragging.current && (Math.abs(gesture.dx) > 6 || Math.abs(gesture.dy) > 6)) {
      isDragging.current = true;
      setDragEmotion('steam');
      if (menuOpen) closeMenu();
    }
    if (isDragging.current) {
      animMoveRef.current?.(e, gesture);
    }
  };

  releaseRef.current = (e, gesture) => {
    if (!isDragging.current) {
      // Tap — toggle menu
      pan.flattenOffset();
      menuOpen ? closeMenu() : openMenu();
    } else {
      // Drag end — cry to corner
      pan.flattenOffset();
      const corner = nearestCorner(pan.x._value, pan.y._value, cfg.wrap);
      setDragEmotion('crying');
      Animated.spring(pan, { toValue: corner, useNativeDriver: false, tension: 50, friction: 7 })
        .start(({ finished }) => {
          if (!finished) return;
          setDragEmotion('melting');
          settleTimer.current = setTimeout(() => setDragEmotion(null), 3000);
        });
    }
    isDragging.current = false;
  };

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder:  () => true,
      onPanResponderGrant:   (...a) => grantRef.current?.(...a),
      onPanResponderMove:    (...a) => moveRef.current?.(...a),
      onPanResponderRelease: (...a) => releaseRef.current?.(...a),
    })
  ).current;

  useEffect(() => () => { if (settleTimer.current) clearTimeout(settleTimer.current); }, []);

  const baseEmotion    = selectEmotion({ isStreakAlive, currentStreak, enrollmentCount, unreadCount, quizScore, isLoading, hasError });
  const displayEmotion = dragEmotion ?? manualEmotion ?? baseEmotion;
  const bodyStyle      = useBodyAnim(displayEmotion);

  // Handlers
  const handleSizeChange = useCallback(async (s) => {
    setSize(s);
    await AsyncStorage.setItem(SK.SIZE, s).catch(() => {});
  }, []);

  const handleHide = useCallback(async () => {
    closeMenu();
    setTimeout(() => setIsHidden(true), 200);
    await AsyncStorage.setItem(SK.HIDDEN, 'true').catch(() => {});
  }, [closeMenu]);

  const handleUnhide = useCallback(async () => {
    setIsHidden(false);
    await AsyncStorage.removeItem(SK.HIDDEN).catch(() => {});
  }, []);

  const handleMoodSelect = useCallback(async (pose) => {
    setManualEmotion(pose);
    await AsyncStorage.setItem(SK.EMOTION, pose).catch(() => {});
  }, []);

  const handleMoodAuto = useCallback(async () => {
    setManualEmotion(null);
    await AsyncStorage.removeItem(SK.EMOTION).catch(() => {});
  }, []);

  const handleSaveGoal = useCallback(async (text) => {
    setGoal(text);
    await AsyncStorage.setItem(SK.GOAL, text).catch(() => {});
  }, []);

  if (isHidden) {
    return (
      <TouchableOpacity
        onPress={handleUnhide}
        activeOpacity={0.8}
        style={[
          hs.unhideBtn,
          Platform.OS === 'web' && { position: 'fixed' },
          {
            backgroundColor: isDark ? 'rgba(9,9,14,0.92)'     : 'rgba(255,255,255,0.95)',
            borderColor:     isDark ? 'rgba(255,107,53,0.35)' : 'rgba(255,107,53,0.45)',
          },
        ]}
      >
        <Image source={require('../../assets/mascot/neutral.png')} style={hs.unhideImg} />
        <Text style={[hs.unhideTxt, { color: '#FF6B35' }]}>Spherix</Text>
      </TouchableOpacity>
    );
  }

  const menuScale = menuAnim.interpolate({ inputRange:[0,1], outputRange:[0.88,1] });
  const menuTY    = menuAnim.interpolate({ inputRange:[0,1], outputRange:[18,0] });

  return (
    <>
      {/* ── Blur backdrop (Modal) ─────────────────────────────────────────── */}
      {menuOpen && (
        <Modal transparent animationType="none" visible={menuOpen} onRequestClose={closeMenu}>
          <TouchableWithoutFeedback onPress={closeMenu}>
            <View style={[StyleSheet.absoluteFill, ms.backdrop, {
              backgroundColor: isDark ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.25)',
            }]} />
          </TouchableWithoutFeedback>

          <Animated.View style={[
            ms.menuContainer,
            {
              bottom: menuPos.current.bottom,
              right:  menuPos.current.right,
              opacity: menuAnim,
              transform: [{ scale: menuScale }, { translateY: menuTY }],
            },
          ]}>
            <SpherixMenu
              displayEmotion={displayEmotion}
              enrollments={enrollments}
              courses={courses}
              size={size}
              onSizeChange={handleSizeChange}
              onHide={handleHide}
              onClose={closeMenu}
              isDark={isDark}
              emotionReason={getEmotionReason(baseEmotion, { currentStreak, enrollmentCount, unreadCount, quizScore })}
            />
          </Animated.View>
        </Modal>
      )}

      {/* ── Mascot bubble ─────────────────────────────────────────────────── */}
      <Animated.View
        style={[
          ms.container,
          { transform: pan.getTranslateTransform() },
          Platform.OS === 'web' && { position: 'fixed' },
        ]}
        {...panResponder.panHandlers}
      >
        <View style={{ width: cfg.wrap, height: cfg.wrap, alignItems:'center', justifyContent:'center', overflow:'visible' }}>
          <Animated.View style={[{ width: cfg.image, height: cfg.image }, bodyStyle]}>
            <Image key={displayEmotion} source={POSES[displayEmotion]} style={{ width: cfg.image, height: cfg.image }} resizeMode="contain" />
          </Animated.View>
          <ParticleOverlay key={displayEmotion} emotion={displayEmotion} wrapSize={cfg.wrap} />
        </View>
      </Animated.View>
    </>
  );
}

// ── Menu styles (layout only — colors applied inline) ─────────────────────────
const mu = StyleSheet.create({
  card: {
    width:        MENU_W,
    borderRadius: 22,
    borderWidth:  1,
    overflow:     'hidden',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 0 0 1px rgba(255,107,53,0.1), 0 24px 80px rgba(0,0,0,0.7), 0 0 60px rgba(255,107,53,0.05)',
    }),
  },
  header: {
    flexDirection:     'row',
    alignItems:        'center',
    padding:           12,
    paddingBottom:     10,
    borderBottomWidth: 1,
    gap:               10,
  },
  headerGlow: {
    width:          44,
    height:         44,
    borderRadius:   22,
    borderWidth:    1.5,
    alignItems:     'center',
    justifyContent: 'center',
    overflow:       'hidden',
  },
  headerImg:  { width: 40, height: 40 },
  headerText: { flex: 1 },
  headerName: { fontSize: 12, fontWeight: '900', letterSpacing: 2.5 },
  headerSub:  { fontSize: 10, marginTop: 2 },
  closeBtn: {
    width:          26,
    height:         26,
    borderRadius:   13,
    alignItems:     'center',
    justifyContent: 'center',
  },
  closeTxt: { fontSize: 13, fontWeight: '700', lineHeight: 18 },

  body: { flexDirection: 'row', height: 300 },

  chatPanel: { flex: 1.1, padding: 10 },
  bubbleRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 6, marginBottom: 8 },
  bubbleAvatar: {
    width:          22,
    height:         22,
    borderRadius:   11,
    alignItems:     'center',
    justifyContent: 'center',
    overflow:       'hidden',
    flexShrink:     0,
    marginTop:      2,
  },
  bubbleAvatarImg: { width: 20, height: 20 },
  bubble: {
    flex:                1,
    borderRadius:        12,
    borderTopLeftRadius: 4,
    padding:             8,
    borderWidth:         1,
  },
  bubbleText: { fontSize: 11.5, lineHeight: 16 },

  dotsRow: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4 },
  dot:     { width: 6, height: 6, borderRadius: 3, opacity: 0.85 },

  dividerV: { width: 1 },

  cardsPanel: { flex: 1, padding: 8 },
  cardsSectionLabel: { fontSize: 8.5, fontWeight: '800', letterSpacing: 2, marginBottom: 5, marginTop: 2 },
  qCard: {
    flexDirection:     'row',
    alignItems:        'center',
    borderRadius:      9,
    borderWidth:       1,
    paddingHorizontal: 8,
    paddingVertical:   8,
    marginBottom:      4,
    gap:               4,
  },
  qCardText:    { flex: 1, fontSize: 11, fontWeight: '600', lineHeight: 15 },
  qCardChevron: { fontSize: 14, fontWeight: '300' },

  footer: {
    flexDirection:     'row',
    alignItems:        'center',
    justifyContent:    'space-between',
    paddingHorizontal: 12,
    paddingVertical:   10,
    borderTopWidth:    1,
  },
  hideBtn:     { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 9, borderWidth: 1 },
  hideBtnText: { fontSize: 11, fontWeight: '700', letterSpacing: 0.3 },

  sizeRow:     { flexDirection: 'row', alignItems: 'center', gap: 6 },
  sizeLabel:   { fontSize: 11, fontWeight: '600' },
  sizeButtons: { flexDirection: 'row', gap: 4 },
  sizeBtn: {
    width:          26,
    height:         24,
    borderRadius:   7,
    borderWidth:    1,
    alignItems:     'center',
    justifyContent: 'center',
  },
  sizeBtnText: { fontSize: 10, fontWeight: '700' },
});

// ── Unhide button styles ──────────────────────────────────────────────────────
const hs = StyleSheet.create({
  unhideBtn: {
    position:        'absolute',
    bottom:          90,
    right:           16,
    zIndex:          9999,
    flexDirection:   'row',
    alignItems:      'center',
    backgroundColor: 'rgba(9,9,14,0.92)',
    borderRadius:    20,
    borderWidth:     1,
    borderColor:     'rgba(255,107,53,0.35)',
    paddingHorizontal: 10,
    paddingVertical:   6,
    gap:             6,
  },
  unhideImg: {
    width:  26,
    height: 26,
    resizeMode: 'contain',
  },
  unhideTxt: {
    color:      '#FF6B35',
    fontSize:   12,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
});

// ── Screen overlay styles ─────────────────────────────────────────────────────
const ms = StyleSheet.create({
  container: {
    position: 'absolute',
    top:      0,
    left:     0,
    zIndex:   9999,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    ...(Platform.OS === 'web' && { backdropFilter: 'blur(10px)' }),
  },
  menuContainer: {
    position: 'absolute',
  },
});
